/**
 * Session-stash helpers for the superadmin emulate-user flow.
 *
 * The stash holds the admin's tokens (so we can restore on exit) plus
 * the audit_id and emulation metadata for banner rendering. It lives in
 * sessionStorage — tab-scoped, evaporates on tab close. That's
 * deliberate: closing the tab is an implicit "stop emulating", and the
 * sweep-stale-impersonations cron closes the audit row when the session
 * times out.
 *
 * Defensive properties:
 *   - JSON.parse failures clear the stash rather than crash.
 *   - A version key (v1) gates future schema changes; old stashes get
 *     ignored if the shape evolves.
 *   - The stash holds short-lived JWTs; sessionStorage scope is enough.
 *     localStorage would survive tab close and outlive the audit row.
 */

const STASH_KEY = '__buildit_impersonation_v1';

export interface ImpersonationStash {
  audit_id: string;
  admin_access_token: string;
  admin_refresh_token: string;
  admin_user_id: string;
  admin_email: string;
  target_user_id: string;
  target_email: string;
  started_at: string;
  read_only: boolean;
  reason: string;
}

export function readImpersonationStash(): ImpersonationStash | null {
  try {
    const raw = sessionStorage.getItem(STASH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ImpersonationStash;
    // Minimal shape check — anything missing means the stash is from a
    // different release or has been tampered with; treat as absent.
    if (
      !parsed.audit_id ||
      !parsed.admin_access_token ||
      !parsed.admin_refresh_token ||
      !parsed.target_user_id
    ) {
      sessionStorage.removeItem(STASH_KEY);
      return null;
    }
    return parsed;
  } catch {
    sessionStorage.removeItem(STASH_KEY);
    return null;
  }
}

export function writeImpersonationStash(stash: ImpersonationStash): void {
  sessionStorage.setItem(STASH_KEY, JSON.stringify(stash));
}

export function clearImpersonationStash(): void {
  sessionStorage.removeItem(STASH_KEY);
}
